/**
 * LMS Application
 * Main application component with routing and authentication
 *
 * Routing note: every feature screen is code-split via React.lazy so a phone
 * only downloads the chunks for the role that is actually signed in. The shell
 * (SystemLayout / header / navigation) stays eager – it renders on first paint.
 */

import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { Spin } from 'antd';

// Core imports
import { canUserLogin, getAccessToken, getUserCredentials } from './core/auth';
import { webSocketService, useChatState, useNetworkStatus } from './core';
import { syncPushSubscription } from './core/pwa';
import { initNative } from './core/native';

// Layout (eager – part of the app shell)
import { SystemLayout } from './shared/layout';
import { NotFound, OfflineBanner } from './shared/components';

// Auth feature (eager – Login is the entry point for unauthenticated users)
import Login from './features/auth/Login';

const ForgotPassword = lazy(() => import('./features/auth/ForgotPassword'));
const PasswordResetConfirm = lazy(() => import('./features/auth/PasswordResetConfirm'));
const ForcePasswordChange = lazy(() => import('./features/auth/ForcePasswordChange'));

// Admin features
const AdminDashboard = lazy(() => import('./features/admin/dashboard/AdminDashboard'));
const CourseList = lazy(() => import('./features/admin/courses/CourseList'));
const GroupList = lazy(() => import('./features/admin/groups/GroupList'));
const ActionsInGroup = lazy(() => import('./features/admin/groups/ActionsInGroup'));
const LessonScheduleList = lazy(() => import('./features/admin/lesson-schedule/list-view/LessonScheduleList'));
const TimetableView = lazy(() => import('./features/admin/lesson-schedule/time-table-view/TimetableView'));
const UserList = lazy(() => import('./features/admin/users/UserList'));
const RoomList = lazy(() => import('./features/admin/rooms/RoomList'));
const AdminUpdates = lazy(() => import('./features/admin/updates/AdminUpdates'));
const AttendanceBoard = lazy(() => import('./features/admin/attendance/AttendanceBoard'));
const AttendanceRoster = lazy(() => import('./features/admin/attendance/AttendanceRoster'));
const DebtorList = lazy(() => import('./features/admin/tuition/DebtorList'));
const StudentStatement = lazy(() => import('./features/admin/tuition/StudentStatement'));
const ParentLinkBoard = lazy(() => import('./features/admin/parent-links/ParentLinkBoard'));
const Settings = lazy(() => import('./features/admin/settings/Settings'));
const MockExamManagement = lazy(() => import('./features/admin/mock-exams/MockExamManagement'));

// Teacher features
const TeacherGroupList = lazy(() => import('./features/teacher/groups/TeacherGroupList'));
const TeacherTaskList = lazy(() => import('./features/teacher/groups/task-list/TeacherTaskList'));
const HomeworkList = lazy(() => import('./features/teacher/groups/task-list/homework-list/TeacherHomeworkList'));
const TeacherLessons = lazy(() => import('./features/teacher/lesson-schedule/TeacherLessons'));
const LessonInstanceManagement = lazy(() => import('./features/teacher/lesson-schedule/LessonInstanceManagement'));
const QuestionBankHierarchy = lazy(() => import('./features/teacher/question-bank/QuestionBankHierarchy'));
const PracticeTests = lazy(() => import('./features/teacher/practice-tests/PracticeTests'));
const Analytics = lazy(() => import('./features/teacher/analytics/Analytics'));

// Student features
const StudentDashboard = lazy(() => import('./features/student/dashboard/StudentDashboard'));
const StudentGroupList = lazy(() => import('./features/student/groups/StudentGroupList'));
const StudentHomeworkList = lazy(() => import('./features/student/groups/task-list/StudentHomeworkList'));
const StudentLessons = lazy(() => import('./features/student/lesson-schedule/StudentLessons'));
const StudentLessonInstances = lazy(() => import('./features/student/lesson-schedule/StudentLessonInstances'));
const StudentPracticeTests = lazy(() => import('./features/student/practice-tests/StudentPracticeTests'));
const StudentPerformance = lazy(() => import('./features/student/performance/StudentPerformance'));

// Shared features
const ChatInterface = lazy(() => import('./features/messages/ChatInterface'));
const UserDashboard = lazy(() => import('./features/dashboard/UserDashboard'));
const UserProfile = lazy(() => import('./features/user-profile/UserProfile'));
const Ranking = lazy(() => import('./features/ranking/Ranking'));
const Resources = lazy(() => import('./features/resources/Resources'));

/**
 * Fallback shown while a route chunk is in flight. Kept deliberately light –
 * on a phone this is usually visible for only a frame or two.
 */
const RouteFallback = () => (
  <div className="lms-route-fallback">
    <Spin size="large" />
  </div>
);

const App = () => {
  const chatState = useChatState();
  const { isConnected, setIsConnected } = chatState;
  const { isOnline, isSyncing } = useNetworkStatus();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const user = getUserCredentials();

  const checkAuth = () => {
    setIsLoading(true);
    const canLogin = canUserLogin();
    setIsAuthenticated(canLogin);
    setIsLoading(false);
  };

  useEffect(() => {
    checkAuth();
  }, []);

  // Native shell wiring (status bar, splash, back button). No-op on the web.
  useEffect(() => {
    initNative();
  }, []);

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      // A first-attempt failure is fine — StompJS keeps retrying with backoff.
      webSocketService.connect(getAccessToken()).catch((error) => {
        console.warn('Initial WebSocket connect failed, will retry', error?.message);
      });

      syncPushSubscription(user?.id).catch((error) => {
        console.warn('Push subscription sync failed', error);
      });
    }

    return () => {
      webSocketService.disconnect();
    };
  }, [isLoading, isAuthenticated, user?.id]);

  useEffect(() => {
    webSocketService.addConnectionListener(setIsConnected);
    return () => webSocketService.removeConnectionListener(setIsConnected);
  }, [setIsConnected]);

  if (isLoading) {
    return <Spin fullscreen />;
  }

  // Unauthenticated routes
  if (!isAuthenticated) {
    return (
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<PasswordResetConfirm />} />
            <Route path="*" element={<Navigate to="/login" />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    );
  }

  // An admin issued this account a temporary password. Nothing else in the app
  // is reachable until it is replaced — the server refuses those requests
  // anyway, so routing anywhere else would only produce 403s on random screens.
  //
  // Wrapped in a Router because ChangePassword (mounted below) calls
  // useNavigate() for its post-change logout redirect — without a router
  // ancestor this branch threw on mount, which took the whole onboarding path
  // down with it. The unauthenticated and authenticated branches each supply
  // their own; this one had none.
  if (user?.mustChangePassword) {
    return (
      <BrowserRouter>
        <Suspense fallback={<RouteFallback />}>
          <ForcePasswordChange />
        </Suspense>
      </BrowserRouter>
    );
  }

  // Authenticated routes
  return (
    <BrowserRouter>
      <OfflineBanner isOnline={isOnline} isConnected={isConnected} isSyncing={isSyncing} />
      <Routes>
        <Route path="/login" element={<Navigate to="/" />} />
        <Route path="/" element={<SystemLayout user={user} isConnected={isConnected} />}>

          {/* Admin Routes */}
          {user.roleName === 'ADMIN' && (
            <>
              <Route index element={<Navigate to="/admin/dashboard" />} />
              <Route path="admin/dashboard" element={<AdminDashboard />} />
              <Route path="admin/courses" element={<CourseList />} />
              <Route path="admin/groups" element={<GroupList />} />
              <Route path="admin/groups/:groupId" element={<ActionsInGroup />} />
              <Route path="admin/lesson-schedules" element={<LessonScheduleList />} />
              <Route path="admin/lesson-schedules/time-table" element={<TimetableView />} />
              <Route path="admin/attendance" element={<AttendanceBoard />} />
              <Route path="admin/attendance/lesson/:lessonInstanceId" element={<AttendanceRoster />} />
              <Route path="admin/tuition" element={<DebtorList />} />
              <Route path="admin/tuition/students/:studentId" element={<StudentStatement />} />
              <Route path="admin/parent-links" element={<ParentLinkBoard />} />
              <Route path="admin/users" element={<UserList />} />
              <Route path="admin/updates" element={<AdminUpdates />} />
              <Route path="admin/rooms" element={<RoomList />} />
              <Route path="admin/mock-exams" element={<MockExamManagement />} />
              <Route path="admin/messages" element={<ChatInterface />} />
              <Route path="admin/profile" element={<UserProfile user={user} />} />
              <Route path="admin/settings" element={<Settings />} />
            </>
          )}

          {/* Teacher Routes */}
          {user.roleName === 'TEACHER' && (
            <>
              <Route index element={<Navigate to="/teacher/dashboard" />} />
              <Route path="teacher/dashboard" element={<UserDashboard user={user} />} />
              <Route path="teacher/profile" element={<UserProfile user={user} />} />
              <Route path="teacher/groups" element={<TeacherGroupList />} />
              <Route path="teacher/groups/:groupId/tasks" element={<TeacherTaskList />} />
              <Route path="teacher/groups/:groupId/lessons" element={<LessonInstanceManagement />} />
              <Route path="teacher/groups/:groupId/tasks/:taskId/homework" element={<HomeworkList />} />
              <Route path="teacher/messages" element={<ChatInterface />} />
              <Route path="teacher/lessons" element={<TeacherLessons />} />
              <Route path="teacher/question-bank" element={<QuestionBankHierarchy />} />
              <Route path="teacher/practice-tests" element={<PracticeTests />} />
              <Route path="teacher/analytics" element={<Analytics />} />
              <Route path="teacher/settings" element={<Settings />} />
            </>
          )}

          {/* Student Routes */}
          {user.roleName === 'STUDENT' && (
            <>
              <Route index element={<Navigate to="/student/dashboard" />} />
              <Route path="student/dashboard" element={<StudentDashboard user={user} />} />
              <Route path="student/profile" element={<UserProfile user={user} />} />
              <Route path="student/subjects" element={<StudentGroupList />} />
              <Route path="student/subjects/:groupId/tasks" element={<StudentHomeworkList />} />
              <Route path="student/subjects/:groupId/lessons" element={<StudentLessonInstances />} />
              <Route path="student/messages" element={<ChatInterface />} />
              <Route path="student/lessons" element={<StudentLessons />} />
              <Route path="student/practice-tests" element={<StudentPracticeTests />} />
              <Route path="student/performance" element={<StudentPerformance />} />
              <Route path="student/settings" element={<Settings />} />
            </>
          )}

          {/* Shared Routes */}
          <Route path="settings" element={<Settings />} />
          <Route path="ranking" element={<Ranking />} />
          <Route path="resources" element={<Resources />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default App;
